import { useId, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { map } from './core/MapView';
import { normalizeIconScale, useAttributePreference } from '../common/util/preferences';
import { findFonts } from './core/mapUtil';

const MapMarkers = ({ markers = [], showTitles, iconScaleOverride }) => {
  const id = useId();

  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const preferredIconScale = useAttributePreference('iconScale', desktop ? 0.75 : 1);
  const iconScale = normalizeIconScale(iconScaleOverride ?? preferredIconScale, desktop ? 0.75 : 1);

  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const focusMode = useSelector((state) => state.devices.focusMode);

  const visibleMarkers = useMemo(() => {
    if (!focusMode) return markers;
    return markers.filter((marker) => marker?.deviceId === selectedDeviceId);
  }, [markers, focusMode, selectedDeviceId]);

  useEffect(() => {
    if (!map) return;

    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(id)) map.removeSource(id);

    map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    if (showTitles) {
      map.addLayer({
        id,
        type: 'symbol',
        source: id,
        filter: ['!has', 'point_count'],
        layout: {
          'icon-image': '{image}',
          'icon-size': iconScale,
          'icon-allow-overlap': true,
          'text-field': '{title}',
          'text-allow-overlap': true,
          'text-anchor': 'bottom',
          'text-offset': [0, -2 * iconScale],
          'text-font': findFonts(map),
          'text-size': 18,
        },
        paint: {
          'text-halo-color': 'white',
          'text-halo-width': 1,
        },
      });
    } else {
      map.addLayer({
        id,
        type: 'symbol',
        source: id,
        layout: {
          'icon-image': '{image}',
          'icon-size': iconScale,
          'icon-allow-overlap': true,
        },
      });
    }

    return () => {
      if (!map) return;
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    };
  }, [id, showTitles, iconScale]);

  useEffect(() => {
    if (!map) return;

    const source = map.getSource(id);
    if (!source) return;

    source.setData({
      type: 'FeatureCollection',
      features: visibleMarkers
        .filter(
          (marker) =>
            Number.isFinite(marker?.latitude)
            && Number.isFinite(marker?.longitude),
        )
        .map(({ latitude, longitude, image, title, deviceId }) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [longitude, latitude],
          },
          properties: {
            image: image || 'default-neutral',
            title: title || '',
            deviceId: deviceId ?? null,
          },
        })),
    });
  }, [id, visibleMarkers]);

  return null;
};

export default MapMarkers;
